import { Link } from "react-router-dom";
import { cn } from "~/lib/utils";

export interface PreviewItem {
  key: string;
  label: string;
  leading: React.ReactNode;
  to?: string;
}

interface PreviewRowProps {
  items: PreviewItem[];
  className?: string;
  // Caps how many item pills render inline before the rest collapse into a
  // "+N" tag. Omit to render every item.
  maxPreview?: number;
}

// A horizontal run of pills previewing a list of items. Items with a `to`
// link out to their full page; others render as static pills.
export const PreviewRow = ({ items, className, maxPreview }: PreviewRowProps) => {
  const shown = maxPreview !== undefined ? items.slice(0, maxPreview) : items;
  const overflow = items.length - shown.length;
  const pillClassName =
    "inline-flex items-center gap-1.5 rounded-full border border-border bg-background px-2 py-0.5";

  return (
    <div className={cn("flex flex-wrap items-center gap-1.5", className)}>
      {shown.map((item) => {
        const content = (
          <>
            {item.leading}
            <span className="max-w-[9rem] truncate text-xs text-foreground">{item.label}</span>
          </>
        );
        return item.to ? (
          <Link
            key={item.key}
            to={item.to}
            className={cn(pillClassName, "transition-colors hover:bg-accent")}
          >
            {content}
          </Link>
        ) : (
          <span key={item.key} className={pillClassName}>
            {content}
          </span>
        );
      })}
      {overflow > 0 && <span className="text-xs text-muted-foreground">+{overflow} more</span>}
    </div>
  );
};
